import { ApiError, operations, type OperationKey, type OpInput, type OpOutput } from '@estudio/contracts';
import type { Transport } from './transport.ts';

export type OpInputLoose<K extends OperationKey> = Omit<OpInput<K>, 'operation_id'> & { operation_id?: string };

export interface CloudClient {
  call<K extends OperationKey>(key: K, input: OpInputLoose<K>, opts?: { signal?: AbortSignal }): Promise<OpOutput<K>>;
  readonly transport: Transport;
}

export function newOperationId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * Cliente tipado del contrato. Valida la entrada con el mismo esquema que el
 * servidor y añade operation_id a las escrituras si falta (para reintentos
 * idempotentes, el llamador debe fijarlo y reutilizarlo).
 */
export function createCloudClient(transport: Transport): CloudClient {
  return {
    transport,
    async call(key, input, opts) {
      const def = operations[key];
      const withOp = def.mutation && (input as { operation_id?: string }).operation_id === undefined && 'operation_id' in def.input.shape
        ? { ...input, operation_id: newOperationId() }
        : input;
      const parsed = def.input.safeParse(withOp);
      if (!parsed.success) {
        throw new ApiError({
          code: 'VALIDATION_FAILED',
          message: 'Datos inválidos',
          retryable: false,
          details: { issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })) },
        });
      }
      return (await transport.call(key, parsed.data, opts)) as never;
    },
  };
}
